import Tooltip from "@material-ui/core/Tooltip";
import Skeleton from "@material-ui/lab/Skeleton";
import { TFunction } from "i18next";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DeviceType } from "../../redux/devices";
import {
  ActivityStatus,
  FirmwareStatus,
  isVitalsCharging,
  PowerStatus,
  VITALS_LOW_RSSI,
  WhyNotActive,
  WifiStatus,
} from "../../redux/devicesStatuses";
import {
  activityStatusCreator,
  firmwareStatusCreator,
  powerStatusCreator,
  sensorsStatusCreator,
  temperatureCreator,
  wifiStatusCreator,
} from "../../redux/devicesStatuses/selectors";
import { IAppState } from "../../redux/types";
import { DateHelper } from "../../utils/DateHelper";
import { MarkForPosture, MarkForVitalsBed } from "./utils";

export const PowerData = React.memo(
  ({
    deviceId,
    t,
    deviceType = DeviceType.vitals,
    showMaintenance = true,
  }: {
    deviceId: string;
    t?: TFunction;
    deviceType?: DeviceType;
    showMaintenance?: boolean;
  }): JSX.Element => {
    const power = useMemo(() => powerStatusCreator(), []);
    const powerSt: PowerStatus = useSelector((state: IAppState) =>
      power(state, deviceId)
    );
    return (
      <>
        <div className="cell">
          <span>{t("labels.Battery")}</span>
          {Object.keys(powerSt).length > 2 && (
            <span className="date-time">
              {DateHelper.LocalTime(powerSt?.datetime, "L, LT")}
            </span>
          )}
        </div>
        {Object.keys(powerSt).length > 1 && (
          <div className="cell">
            {showMaintenance && powerSt?.failure ? (
              <Tooltip title={t("labels.maintenance")}>
                <i className="icon-alert-maintenance" />
              </Tooltip>
            ) : (
              <>
                {deviceType === DeviceType.vitals && (
                  <>
                    {isVitalsCharging(powerSt)
                      ? ""
                      : t("device.Unplugged") + ","}{" "}
                  </>
                )}
                {powerSt.batteryLevel}%
              </>
            )}
          </div>
        )}
      </>
    );
  }
);
PowerData.displayName = "PowerData";
const WifiCreator = ({
  deviceId,
}: {
  deviceId: string;
}): { tooltipLabel: string; item: JSX.Element; key: number | string } => {
  const { t } = useTranslation("common");
  const wifiMemo = useMemo(() => wifiStatusCreator(), []);
  const wifi = useSelector((state: IAppState) => wifiMemo(state, deviceId));

  const validWifi = Object.keys(wifi).length > 1;
  const badStatusMemo = useMemo(() => activityStatusCreator(), []);
  const badStatus = useSelector((state: IAppState) =>
    badStatusMemo(state, deviceId)
  );

  if (badStatus?.isActive)
    return !validWifi
      ? {
          tooltipLabel: t("labels.noWifi"),
          item: <i className="icon-wi-fi-off" />,
          key: "noData" + t("labels.noWifi"),
        }
      : (wifi as WifiStatus)?.rssiDbm <= VITALS_LOW_RSSI
      ? {
          tooltipLabel: t("labels.lowWifi"),
          item: (
            <i className="icon-wi-fi-low">
              <span className="path1"></span>
              <span className="path2"></span>
              <span className="path3"></span>
              <span className="path4"></span>
            </i>
          ),
          key: wifi?.datetime + t("labels.lowWifi"),
        }
      : null;
  return badStatus?.status === WhyNotActive.NO_BATTERY
    ? null
    : {
        tooltipLabel: t("labels.noWifi"),
        item: <i className="icon-wi-fi-off" />,
        key: wifi?.datetime + t("labels.noWifi"),
      };
};
export const MarkForWifi = React.memo(
  ({
    deviceId,
    needToolTip = true,
  }: {
    deviceId: string;
    needToolTip?: boolean;
  }): JSX.Element => {
    const data = WifiCreator({ deviceId });
    if (data === null) return null;
    const { item, tooltipLabel, key } = data;
    return needToolTip ? (
      <Tooltip title={tooltipLabel} key={key}>
        {item}
      </Tooltip>
    ) : (
      <>{item}</>
    );
  }
);
MarkForWifi.displayName = "MarkForWifi";
export const MarkForBluetooth = React.memo(
  ({
    deviceId,
    needToolTip = true,
  }: {
    deviceId: string;
    needToolTip?: boolean;
  }): JSX.Element => {
    const { t } = useTranslation("common");
    const activityMemo = useMemo(() => activityStatusCreator(), []);
    const activity = useSelector((state: IAppState) =>
      activityMemo(state, deviceId)
    );
    const powerMemo = useMemo(() => powerStatusCreator(), []);
    const power = useSelector((state: IAppState) => powerMemo(state, deviceId));
    if (activity?.isActive) return null;
    if (power?.batteryLevel === 0) return null;
    return needToolTip ? (
      <Tooltip title={t("labels.noBluetooth")} key={power?.datetime}>
        <i className="icon-bluetooth-off"></i>
      </Tooltip>
    ) : (
      <>{<i className="icon-bluetooth-off"></i>}</>
    );
  }
);
MarkForBluetooth.displayName = "MarkForBluetooth";
export const DeviceStatePower = React.memo(
  ({
    deviceId,
    deviceType,
  }: {
    deviceId: string;
    deviceType: DeviceType;
  }): JSX.Element => {
    let batteryLevel;
    const powerMemo = useMemo(() => powerStatusCreator(), []);
    const power: any = useSelector((state: IAppState) =>
      powerMemo(state, deviceId)
    );

    const activityMemo = useMemo(() => activityStatusCreator(), []);
    const activity: ActivityStatus = useSelector((state: IAppState) =>
      activityMemo(state, deviceId)
    );
    if (!power) {
      if (deviceType !== DeviceType.watch) return null;
      else
        return (
          <div className="flex-column flex-center">
            <i className={`icon-empty-hor`} />
            <span className="text-size-10">{"0%"}</span>
          </div>
        );
    }
    if (power?.failure) return <i className="icon-alert-maintenance" />;
    //if vitals device is connected to power - no battery
    if (
      deviceType === DeviceType.vitals &&
      !activity?.isActive &&
      activity?.status === WhyNotActive.NO_BATTERY
    ) {
      return (
        <>
          <i className={`icon-empty-hor`} />
          <i className="icon-unplugged" />
        </>
      );
    }
    if (deviceType === DeviceType.vitals && power.connected) return null;
    if (power?.batteryLevel === undefined) return null;
    if (power.batteryLevel === 0) batteryLevel = "empty";
    else if (power.batteryLevel >= 1 && power.batteryLevel <= 30)
      batteryLevel = "low";
    else if (power.batteryLevel >= 31 && power.batteryLevel <= 60)
      batteryLevel = "medium-hor";
    else if (power.batteryLevel >= 60) batteryLevel = "full-hor";
    // if (batteryLevel === '' && deviceType === DeviceType.biopatch) debugger;

    const iIcon =
      batteryLevel === "low" ? (
        <i className="icon-low-hor">
          <span className="path1"></span>
          <span className="path2"></span>
        </i>
      ) : (
        <>
          {batteryLevel !== "" ? (
            <i className={`icon-${batteryLevel}`} />
          ) : (
            <i className={`icon-`} />
          )}
        </>
      );
    // console.log('MarkForPower');
    return deviceType === DeviceType.vitals ? (
      <>
        <i className="icon-unplugged" />
        {batteryLevel !== "" && (
          <div className="flex-column flex-center">
            {iIcon}
            <span className="text-size-10">{power.batteryLevel + "%"}</span>
          </div>
        )}
      </>
    ) : (
      <div className="flex-column flex-center">
        {power?.batteryCharging ? (
          <i className={`icon-battery-charging`} />
        ) : (
          iIcon
        )}
        <span className="text-size-10">{power.batteryLevel + "%"}</span>
      </div>
    );
  }
);
DeviceStatePower.displayName = "DeviceStatePower";
export const MarkForPower = React.memo(
  ({
    deviceId,
    deviceType,
    showDateTime = false,
    showMaintenance = true,
  }: {
    deviceId: string;
    deviceType: DeviceType;
    showDateTime?: boolean;
    showMaintenance?: boolean;
  }): JSX.Element => {
    let batteryLevel;
    const powerMemo = useMemo(() => powerStatusCreator(), []);
    const power: PowerStatus = useSelector((state: IAppState) =>
      powerMemo(state, deviceId)
    );
    const activityMemo = useMemo(() => activityStatusCreator(), []);
    const activity: ActivityStatus = useSelector((state: IAppState) =>
      activityMemo(state, deviceId)
    );

    if (!power && !activity) return null;
    if (showMaintenance && power?.failure)
      return <i className="icon-alert-maintenance" />;
    //if vitals device is connected to power - no battery

    if (!activity?.isActive && activity?.status === WhyNotActive.NO_BATTERY) {
      return (
        <>
          <i className={`icon-battery-empty`} />

          <i className="icon-unplugged" />
        </>
      );
    }
    if (deviceType === DeviceType.vitals && power?.connected) return null;
    if (power?.batteryLevel === undefined) return null;
    if (power.batteryLevel === 0) batteryLevel = "empty";
    else if (power.batteryLevel >= 1 && power.batteryLevel <= 30)
      batteryLevel = "low";
    else if (power.batteryLevel >= 31 && power.batteryLevel <= 60)
      batteryLevel = "medium";
    else if (power.batteryLevel >= 60) batteryLevel = "";
    // console.log('MarkForPower');
    const iIcon =
      batteryLevel === "low" ? (
        <i className="icon-battery-low">
          <span className="path1"></span>
          <span className="path2"></span>
        </i>
      ) : (
        <>
          {batteryLevel !== "" ? (
            <i className={`icon-battery-${batteryLevel}`} />
          ) : (
            <i className={`icon-battery`} />
          )}
        </>
      );
    return deviceType === DeviceType.vitals ? (
      <>
        <i className="icon-unplugged" />

        {batteryLevel !== "" && { iIcon }}
      </>
    ) : (
      iIcon
    );
  }
);
MarkForPower.displayName = "MarkForPower";
export const MarkForActivity = React.memo(
  ({
    deviceId,
    includeToolTip = true,
  }: {
    deviceId: string;
    includeToolTip?: boolean;
  }): JSX.Element => {
    const activityMemo = useMemo(() => activityStatusCreator(), []);
    const activity = useSelector((state: IAppState) =>
      activityMemo(state, deviceId)
    );
    return activity?.isActive ? (
      <div className="mark mark-active" />
    ) : (
      <div className="mark mark-empty" />
    );
  }
);
MarkForActivity.displayName = "MarkForActivity";
export const MarkForTemperature = React.memo(
  ({ deviceIds, t }: { deviceIds: string[]; t: TFunction }): JSX.Element => {
    const tempMemo = useMemo(() => temperatureCreator(), []);
    const temp = useSelector((state: IAppState) => tempMemo(state, deviceIds));
    return temp ? (
      <span className="temp">{temp + t("C").replace(" ", "")}</span>
    ) : null;
  }
);
MarkForTemperature.displayName = "MarkForTemperature";
export const DisplayResidentsDevices = React.memo(
  ({
    deviceIds,
    showIcon = false,
    residentId,
  }: {
    deviceIds: string[];
    showIcon?: boolean;
    residentId: string;
  }): JSX.Element => {
    // console.log('DisplayResidentsDevices');

    return (
      <>
        {deviceIds.map((el, index) => {
          return (
            <div
              key={index}
              className={`one-device-icons ${showIcon ? "align-mobile" : ""}`}
            >
              <OneDeviceIcons
                deviceId={el}
                index={index}
                showIcon={showIcon}
                residentId={residentId}
              />
            </div>
          );
        })}
      </>
    );
  }
);
DisplayResidentsDevices.displayName = "DisplayResidentsDevices";
export const OneDeviceIcons = React.memo(
  ({
    deviceId,
    index,
    showIcon = false,
    residentId = undefined,
    hideStatus = false,
  }: {
    deviceId: string;
    index: number;
    showIcon?: boolean;
    residentId?: string;
    hideStatus?: boolean;
  }): JSX.Element => {
    const device = useSelector(
      (state: IAppState) => state.devices.devices[deviceId]
    );
    const activityMemo = useMemo(() => activityStatusCreator(), []);
    const activity = useSelector((state: IAppState) =>
      activityMemo(state, deviceId)
    );
    const isDevicesStatusesLoaded =
      useSelector((state: IAppState) => state.devicesStatuses.isLoading) ===
      false;
    return device === undefined ? null : !isDevicesStatusesLoaded ? (
      <>
        <Skeleton variant="circle" className="skeleton-table-dot" />
        <Skeleton variant="rect" className="skeleton-table-square" />
        <Skeleton variant="rect" className="skeleton-table-square" />
      </>
    ) : (
      <React.Fragment key={index}>
        {!hideStatus && <MarkForActivity deviceId={deviceId} />}

        {device.type === DeviceType.vitals && residentId && (
          <>
            <MarkForVitalsBed residentId={residentId} vitalsId={deviceId} />
            <MarkForWifi deviceId={deviceId} />
            <MarkForPower deviceId={deviceId} deviceType={device.type} />
          </>
        )}
        {device.type === DeviceType.biopatch && residentId && (
          <>
            {activity?.isActive ? (
              <>
                <MarkForPosture biopatchId={deviceId} />
                <MarkForBluetooth deviceId={deviceId} />
                <DeviceStatePower
                  deviceId={deviceId}
                  deviceType={DeviceType.biopatch}
                />
                {/* <MarkForPower deviceId={deviceId} deviceType={device.type} /> */}
              </>
            ) : (
              <BadBiopatchStatus deviceId={deviceId} />
            )}
          </>
        )}
        {device.type === DeviceType.watch && residentId && (
          <>
            {activity?.isActive ? (
              <>
                <DeviceStatePower
                  deviceId={deviceId}
                  deviceType={DeviceType.watch}
                />
              </>
            ) : (
              <BadWatchStatus deviceId={deviceId} />
            )}
          </>
        )}
      </React.Fragment>
    );
  }
);
OneDeviceIcons.displayName = "OneDeviceIcons";
export const BatteryPercent = React.memo(
  ({ deviceId }: { deviceId: string }): JSX.Element => {
    const powerMemo = useMemo(() => powerStatusCreator(), []);
    const power: PowerStatus = useSelector((state: IAppState) =>
      powerMemo(state, deviceId)
    );
    const badStatusMemo = useMemo(() => activityStatusCreator(), []);
    const badStatus = useSelector((state: IAppState) =>
      badStatusMemo(state, deviceId)
    );
    if (power?.failure) {
      console.log("failure: " + deviceId);
    }
    if (power?.failure) return <i className="icon-alert-maintenance" />;
    if (!badStatus?.isActive && badStatus?.status === WhyNotActive.NO_WIFI)
      return null;
    const batteryLevel = power?.batteryLevel?.toString();
    return batteryLevel ? <>{batteryLevel + "%"}</> : null;
  }
);
BatteryPercent.displayName = "BatteryPercent";
export const PirValid = React.memo(
  ({ deviceId }: { deviceId: string }): JSX.Element => {
    const sensorMemo = useMemo(() => sensorsStatusCreator(), []);
    const sensor = useSelector((state: IAppState) =>
      sensorMemo(state, deviceId)
    );
    if (
      sensor === undefined ||
      sensor?.pir === undefined ||
      sensor?.pir === null
    )
      return null;

    const { isConnected } = sensor?.pir;

    const check = <i className="icon-check"></i>;
    if (isConnected) return check;
    return null;
  }
);
PirValid.displayName = "PirValid";

export const ParceICCID = React.memo(
  ({ deviceId }: { deviceId: string }): JSX.Element => {
    const firmMemo = useMemo(() => firmwareStatusCreator(), []);
    const firm = useSelector((state: IAppState) =>
      firmMemo(state, deviceId)
    ) as FirmwareStatus;

    return <>{firm?.iccId || ""}</>;
  }
);
ParceICCID.displayName = "ParceICCID";
export const BadBiopatchStatus = React.memo(
  ({ deviceId }: { deviceId: string }): JSX.Element => {
    const powerMemo = useMemo(() => powerStatusCreator(), []);
    const power: PowerStatus = useSelector((state: IAppState) =>
      powerMemo(state, deviceId)
    );

    if (power?.failure) return <i className="icon-alert-maintenance" />;
    if (power?.batteryLevel && power?.batteryLevel === 0)
      return (
        <>
          <i className={`icon-empty-hor`} />
          <span className="text-size-10">{power.batteryLevel + "%"}</span>
        </>
      );
    return (
      <div className="power-data">
        <i className="icon-bluetooth-off"></i>
      </div>
    );
  }
);
BadBiopatchStatus.displayName = "BadBiopatchStatus";
export const BadWatchStatus = React.memo(
  ({ deviceId }: { deviceId: string }): JSX.Element => {
    const powerMemo = useMemo(() => powerStatusCreator(), []);
    const power: PowerStatus = useSelector((state: IAppState) =>
      powerMemo(state, deviceId)
    );
    const activityMemo = useMemo(() => activityStatusCreator(), []);
    const activity = useSelector((state: IAppState) =>
      activityMemo(state, deviceId)
    );

    return !activity?.isActive && power?.batteryLevel > 2 ? (
      <i className="icon-No-4G" />
    ) : (
      <>
        <i className={`icon-battery-empty`} />
        <span className="text-size-10">{0 + "%"}</span>
      </>
    );
  }
);
BadWatchStatus.displayName = "BadWatchStatus";
export const BadTagStatus = React.memo(
  ({ deviceId }: { deviceId: string }): JSX.Element => {
    const activityMemo = useMemo(() => activityStatusCreator(), []);
    const activity = useSelector((state: IAppState) =>
      activityMemo(state, deviceId)
    );
    return activity?.status === WhyNotActive.NO_BLUETOOTH ? (
      <i className="icon-bluetooth-off"></i>
    ) : (
      <div className="flex-column flex-center">
        <i className={`icon-empty-hor`} />
        <span className="text-size-10">{0 + "%"}</span>
      </div>
    );
  }
);
BadTagStatus.displayName = "BadTagStatus";
export const nearbyOutWG = (size: number): JSX.Element => {
  return (
    <i className={`icon-user-nearby-out-WG font-${size}`}>
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
      <span className="path4"></span>
    </i>
  );
};

export const nearbyInGG = (): JSX.Element => {
  return (
    <i className="icon-user-nearby-in-GG">
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
      <span className="path4"></span>
      <span className="path5"></span>
      <span className="path6"></span>
      <span className="path7"></span>
    </i>
  );
};

export const nearbyInWG = (size: number): JSX.Element => {
  return (
    <i className={`icon-user-nearby-in-WG font-${size}`}>
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
      <span className="path4"></span>
      <span className="path5"></span>
      <span className="path6"></span>
      <span className="path7"></span>
    </i>
  );
};

export const nearbyOutGG = (): JSX.Element => {
  return (
    <i className="icon-user-nearby-out-GG">
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
      <span className="path4"></span>
    </i>
  );
};

export const nearbyBack = (): JSX.Element => {
  return (
    <i className="icon-lying-on-back-near">
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
    </i>
  );
};

export const nearbyLeft = (): JSX.Element => {
  return (
    <i className="icon-lying-left-near">
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
    </i>
  );
};

export const nearbyRight = (size: number): JSX.Element => {
  return (
    <i className={`icon-lying-right-near font-${size}`}>
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
    </i>
  );
};

export const nearbyBelly = (): JSX.Element => {
  return (
    <i className="icon-lying-on-belly-near">
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
    </i>
  );
};

export const nearbyStand = (): JSX.Element => {
  return (
    <i className="icon-out-standing">
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
      <span className="path4"></span>
      <span className="path5"></span>
      <span className="path6"></span>
      <span className="path7"></span>
    </i>
  );
};
export const nearbySit = (): JSX.Element => {
  return (
    <i className="icon-bed-sitting-in-near">
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
      <span className="path4"></span>
      <span className="path5"></span>
    </i>
  );
};

export const nearbyRoomGG = (): JSX.Element => {
  return (
    <i className="icon-user-nearby-room-GG">
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
      <span className="path4"></span>
    </i>
  );
};

export const nearbyRoomWG = (size: number): JSX.Element => {
  return (
    <i className={`icon-user-nearby-room-WG font-${size}`}>
      <span className="path1"></span>
      <span className="path2"></span>
      <span className="path3"></span>
      <span className="path4"></span>
    </i>
  );
};
